import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { Badge, Button, Input, Table } from 'reactstrap';
import Loader from '../../components/layout/Loader';
import CandidateWorkspacePanel from '../../components/CandidateWorkspacePanel';
import { AppContext } from '../../context';
import { GET_NEW_APPLICANTS_COUNT, GET_NEW_APPLICANTS_GROUPED } from './gql';
import { COMPANY_DETAILS_ROUTE, VIEW_JOB_ORDER_ROUTE, SET_STATUS_OPTIONS } from '../../utils/constant';
import AiScoreBadge from './job-order-components/AiScoreBadge';

const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

const relativeDate = (date: string) => moment(date).fromNow();

const COUNT_BADGE_STYLE: React.CSSProperties = {
  background: '#eaa827', color: '#fff', borderRadius: 12, padding: '3px 10px', fontSize: 13, fontWeight: 700,
};

const AVATAR_STYLE: React.CSSProperties = {
  width: 34, height: 34, borderRadius: '50%', background: '#eaa827', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0,
};

const COMPANY_NEW_BADGE_STYLE: React.CSSProperties = {
  background: 'rgba(234,168,39,0.12)', color: '#eaa827', borderRadius: 30, padding: '4px 10px', fontSize: 11, fontWeight: 600, marginRight: 10,
};

const WORK_TYPE_BADGE_STYLE: React.CSSProperties = {
  background: '#e5e9f8', color: '#686e78', borderRadius: 30, padding: '3px 10px', fontSize: 10, fontWeight: 500,
};

const APPLICANTS_COUNT_BADGE_STYLE: React.CSSProperties = {
  background: '#e5e9f8', color: '#686e78', borderRadius: 30, padding: '4px 10px', fontSize: 11, fontWeight: 500,
};

const NewApplicants = () => {
  const { user } = useContext(AppContext);
  const apolloClient = useApolloClient();
  const history = useHistory();

  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [processedIds, setProcessedIds] = useState<Set<string>>(new Set());
  const [selectedApplicant, setSelectedApplicant] = useState<any>(null);
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const processing = useRef(false);

  const { data, loading, error } = useQuery(GET_NEW_APPLICANTS_GROUPED, {
    fetchPolicy: 'cache-and-network',
  });

  const rawGroups: any[] = data?.getNewApplicantsGrouped ?? [];

  const filteredGroups = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return rawGroups
      .filter((group) => !companyFilter || group.company.id === companyFilter)
      .map((group) => ({
        ...group,
        jobOrders: group.jobOrders
          .filter((jo: any) => !jobFilter || jo.jobOrder.id === jobFilter)
          .map((jo: any) => ({
            ...jo,
            applicants: jo.applicants.filter((a: any) => {
              if (processedIds.has(a.id)) return false;
              if (!lowerSearch) return true;
              const name = a.candidate?.fullName?.toLowerCase() ?? '';
              const title = a.candidate?.jobTitle?.toLowerCase() ?? '';
              const company = a.candidate?.companyName?.toLowerCase() ?? '';
              return name.includes(lowerSearch) || title.includes(lowerSearch) || company.includes(lowerSearch);
            }),
          }))
          .filter((jo: any) => jo.applicants.length > 0),
      }))
      .filter((group) => group.jobOrders.length > 0);
  }, [rawGroups, search, companyFilter, jobFilter, processedIds]);

  const allRawCount = rawGroups.reduce(
    (sum, g) =>
      sum +
      g.jobOrders.reduce(
        (s: number, jo: any) =>
          s + jo.applicants.filter((a: any) => !processedIds.has(a.id)).length,
        0
      ),
    0
  );

  useEffect(() => {
    if (rawGroups.length && expandedCompanies.size === 0) {
      setExpandedCompanies(new Set(rawGroups.map((g) => g.company.id)));
    }
  }, [rawGroups.length]);

  const markProcessed = async (applicantId: string) => {
    if (processing.current) return;
    processing.current = true;

    const next = new Set(processedIds).add(applicantId);
    setProcessedIds(next);

    try {
      await apolloClient.refetchQueries({ include: [GET_NEW_APPLICANTS_COUNT] });
      const needsMore = rawGroups.some((g) =>
        g.jobOrders.some((jo: any) => {
          const remaining = jo.applicants.filter((a: any) => !next.has(a.id)).length;
          return remaining === 0 && jo.totalApplicants > jo.applicants.length;
        })
      );
      if (needsMore) {
        await apolloClient.refetchQueries({ include: [GET_NEW_APPLICANTS_GROUPED] });
        setProcessedIds(new Set());
      }
    } finally {
      processing.current = false;
    }
  };

  const clearFilters = () => {
    setSearch('');
    setCompanyFilter('');
    setJobFilter('');
  };

  const hasActiveFilters = search || companyFilter || jobFilter;

  const jobsForCompany = (companyId: string) => {
    const group = rawGroups.find((g) => g.company.id === companyId);
    return group?.jobOrders ?? [];
  };

  if (loading && rawGroups.length === 0) return <Loader />;
  if (error) return <div className="text-center py-5"><h6 className="text-danger">Failed to load applicants. Please refresh.</h6></div>;

  return (
    <div className="admin-tabs team-tabs">
      {/* Page header */}
      <div className="primaryHeading primaryHeaderSpacing d-flex align-items-center justify-content-between">
        <h5 className="m-0">New Applicants</h5>
        {allRawCount > 0 && (
          <span style={COUNT_BADGE_STYLE}>
            {allRawCount} new
          </span>
        )}
      </div>

      {/* Toolbar */}
      <div className="search-filter primaryHeaderSpacing">
        <div className="filter-status filterJobOrder justify-content-between w-100 flex-wrap">
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
            <Input
              placeholder="Search by name, title, company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 280, height: 36 }}
            />
            <Input
              type="select"
              value={companyFilter}
              onChange={(e) => { setCompanyFilter(e.target.value); setJobFilter(''); }}
              style={{ width: 200, height: 36 }}
            >
              <option value="">All Companies</option>
              {rawGroups.map((g) => (
                <option key={g.company.id} value={g.company.id}>{g.company.name}</option>
              ))}
            </Input>
            <Input
              type="select"
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
              disabled={!companyFilter}
              style={{ width: 220, height: 36 }}
            >
              <option value="">All Job Orders</option>
              {companyFilter && jobsForCompany(companyFilter).map((jo: any) => (
                <option key={jo.jobOrder.id} value={jo.jobOrder.id}>{jo.jobOrder.jobTitle}</option>
              ))}
            </Input>
            {hasActiveFilters && (
              <button
                type="button"
                className="btn-job-filters"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Empty states */}
      {allRawCount === 0 && !loading && (
        <div className="text-center py-5">
          <h6 className="text-muted">All caught up! No new applicants waiting for review.</h6>
        </div>
      )}

      {allRawCount > 0 && filteredGroups.length === 0 && (
        <div className="text-center py-5">
          <h6 className="text-muted mb-2">No results match your filters.</h6>
          <button type="button" className="btn-job-filters" onClick={clearFilters}>Clear filters</button>
        </div>
      )}

      {/* Company groups */}
      {filteredGroups.map((group) => {
        const companyId = group.company.id;
        const isExpanded = expandedCompanies.has(companyId);
        const companyCount = group.jobOrders.reduce(
          (s: number, jo: any) => s + jo.totalApplicants, 0
        );

        return (
          <div key={companyId} className="for-position-multiSelecte mb-3">
            {/* Company header row */}
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{
                background: '#fff',
                border: '1px solid #e5e9f8',
                borderRadius: isExpanded ? '6px 6px 0 0' : 6,
                cursor: 'pointer',
              }}
              onClick={() =>
                setExpandedCompanies((prev) => {
                  const next = new Set(prev);
                  isExpanded ? next.delete(companyId) : next.add(companyId);
                  return next;
                })
              }
            >
              <div className="d-flex align-items-center">
                <div
                  className="d-flex align-items-center justify-content-center mr-3"
                  style={AVATAR_STYLE}
                >
                  {getInitials(group.company.name)}
                </div>
                <div>
                  <span
                    className="anchorColorText route-link font-weight-bold text-14"
                    onClick={(e) => { e.stopPropagation(); history.push(`${COMPANY_DETAILS_ROUTE}/${companyId}`); }}
                  >
                    {group.company.name}
                  </span>
                  <span className="text-muted ml-2" style={{ fontSize: 12 }}>
                    {group.jobOrders.length} job order{group.jobOrders.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              <div className="d-flex align-items-center">
                <Badge style={COMPANY_NEW_BADGE_STYLE}>
                  {companyCount} new
                </Badge>
                <span className="text-muted" style={{ fontSize: 11 }}>{isExpanded ? '▲' : '▼'}</span>
              </div>
            </div>

            {/* Job orders */}
            {isExpanded && (
              <div style={{ border: '1px solid #e5e9f8', borderTop: 0, borderRadius: '0 0 6px 6px', background: '#fff' }}>
                {group.jobOrders.map((jo: any, joIdx: number) => {
                  const { jobOrder, applicants } = jo;
                  const loc = jobOrder.companyLocation;
                  const locationStr = loc ? [loc.city, loc.state].filter(Boolean).join(', ') : null;

                  return (
                    <div key={jobOrder.id} style={{ borderTop: joIdx === 0 ? 'none' : '1px solid #e5e9f8' }}>
                      {/* Job order sub-header */}
                      <div
                        className="d-flex align-items-center justify-content-between px-3 py-2"
                        style={{ background: '#f7f8f9' }}
                      >
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                          <span
                            className="anchorColorText route-link font-weight-bold text-14"
                            onClick={() => history.push(`${VIEW_JOB_ORDER_ROUTE}/${jobOrder.id}`)}
                          >
                            {jobOrder.jobTitle}
                          </span>
                          {jobOrder.workType && (
                            <Badge
                              className="text-capitalize"
                              style={WORK_TYPE_BADGE_STYLE}
                            >
                              {jobOrder.workType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase())}
                            </Badge>
                          )}
                          {locationStr && (
                            <span className="text-muted" style={{ fontSize: 12 }}>
                              📍 {locationStr}
                            </span>
                          )}
                        </div>
                        <Badge style={APPLICANTS_COUNT_BADGE_STYLE}>
                          {jo.totalApplicants} applicant{jo.totalApplicants !== 1 ? 's' : ''}
                        </Badge>
                      </div>

                      {/* Applicant table */}
                      <div className="table-responsive">
                        <Table className="mb-0" style={{ fontSize: 13, tableLayout: 'fixed', width: '100%' }}>
                          <colgroup>
                            <col style={{ width: '20%' }} />
                            <col style={{ width: '27%' }} />
                            <col style={{ width: '25%' }} />
                            <col style={{ width: '17%' }} />
                            <col style={{ width: '11%' }} />
                          </colgroup>
                          <thead style={{ background: '#f7f8f9' }}>
                            <tr>
                              <th className="text-uppercase" style={{ fontSize: 11, color: '#8E949D', fontWeight: 500, paddingLeft: 16 }}>Applicant</th>
                              <th className="text-uppercase" style={{ fontSize: 11, color: '#8E949D', fontWeight: 500 }}>Current Title / Employer</th>
                              <th className="text-uppercase" style={{ fontSize: 11, color: '#8E949D', fontWeight: 500 }}>Contact</th>
                              <th className="text-uppercase" style={{ fontSize: 11, color: '#8E949D', fontWeight: 500 }}>Candidate Owner</th>
                              <th className="text-uppercase" style={{ fontSize: 11, color: '#8E949D', fontWeight: 500 }}>Applied</th>
                            </tr>
                          </thead>
                          <tbody>
                            {applicants.map((applicant: any) => {
                              const candidate = applicant.candidate;
                              const primaryEmail = candidate?.emails?.find((e: any) => e.isPrimary)?.email
                                || candidate?.emails?.[0]?.email;
                              const primaryPhone = candidate?.phones?.find((p: any) => p.isPrimary)?.phoneNumber
                                || candidate?.phones?.[0]?.phoneNumber;

                              return (
                                <tr key={applicant.id}>
                                  <td style={{ paddingLeft: 16, verticalAlign: 'middle' }}>
                                    <h6
                                      className="anchorColorText route-link mb-0 d-inline"
                                      onClick={() => setSelectedApplicant({ ...applicant, JobOrder: jobOrder })}
                                    >
                                      {candidate?.fullName || '--'}
                                    </h6>
                                    <AiScoreBadge
                                      jobApplicantId={applicant.id}
                                      candidateId={candidate?.id}
                                      aiScore={applicant.aiScore}
                                      aiScoreReason={applicant.aiScoreReason}
                                      aiScoredResumeId={applicant.aiScoredResumeId}
                                      aiScoredAt={applicant.aiScoredAt}
                                    />
                                  </td>
                                  <td style={{ verticalAlign: 'middle' }}>
                                    <div className="oneLineClampText">{candidate?.jobTitle || '--'}</div>
                                    <div className="subtitleText">{candidate?.companyName || '--'}</div>
                                  </td>
                                  <td style={{ verticalAlign: 'middle', overflow: 'hidden' }}>
                                    <div style={{ color: '#424242', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primaryEmail || '--'}</div>
                                    {primaryPhone && (
                                      <div style={{ color: '#686e78', fontSize: 12 }}>{primaryPhone}</div>
                                    )}
                                  </td>
                                  <td style={{ color: '#424242', verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {candidate?.recruiterOwner?.fullName
                                      || (Array.isArray(candidate?.candidateStats)
                                        ? candidate?.candidateStats?.[0]
                                        : candidate?.candidateStats)?.recruitingOwner?.fullName
                                      || '--'}
                                  </td>
                                  <td style={{ color: '#686e78', verticalAlign: 'middle', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {applicant.createdAt ? relativeDate(applicant.createdAt) : '--'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Candidate Workspace Panel */}
      {selectedApplicant && (
        <CandidateWorkspacePanel
          isOpen={!!selectedApplicant}
          toggle={() => setSelectedApplicant(null)}
          applicant={selectedApplicant}
          setStatusOptions={SET_STATUS_OPTIONS}
          onStatusChange={(id) => {
            markProcessed(id);
            setSelectedApplicant(null);
          }}
          onProcessed={(id) => {
            markProcessed(id);
          }}
          onDeclined={(id) => {
            markProcessed(id);
            setSelectedApplicant(null);
          }}
        />
      )}
    </div>
  );
};

export default NewApplicants;
